import { Controller } from "@hotwired/stimulus";
import flatpickr from "flatpickr";

export default class extends Controller {
  static values = {
    enableTime: { type: Boolean, default: false },
    dateFormat: { type: String, default: "" },
    minDate: { type: String, default: "" },
    // Puts back a typed date the picker refused (before minDate, unparsable), so the server can say why instead of saving nothing.
    keepTyped: { type: Boolean, default: false },
  }

  connect() {
    this.dialog = this.element.closest("dialog")
    this.handleDialogClose = this.closePicker.bind(this)

    const enableTime = this.enableTimeValue
    const dateFormat = this.dateFormatValue || (enableTime ? "Y-m-d H:i" : "Y-m-d")

    this.picker = flatpickr(this.element, {
      enableTime,
      time_24hr: true,
      dateFormat,
      minDate: this.minDateValue || undefined,
      // Read typed dates strictly: the default parser took 31.12.2027 as 2027-01-01 and rolled 2027-02-31 over (QA).
      parseDate: this.keepTypedValue ? (text) => this.parseTyped(text) : undefined,
      allowInput: true,
      disableMobile: true,
      appendTo: this.dialog || undefined,
      positionElement: this.element,
      position: this.dialog ? "above start" : "auto",
      onReady: (_selectedDates, _dateStr, instance) => this.decorateCalendar(instance),
      onOpen: (_selectedDates, _dateStr, instance) => {
        this.decorateCalendar(instance)
        if (this.dialog && instance.calendarContainer) {
          const inputRect = this.element.getBoundingClientRect()
          const dialogRect = this.dialog.getBoundingClientRect()
          const calendarHeight = instance.calendarContainer.offsetHeight
          instance.calendarContainer.style.left = `${inputRect.left - dialogRect.left}px`
          instance.calendarContainer.style.top = `${inputRect.top - dialogRect.top - calendarHeight - 4}px`
        }
      }
    });

    this.dialog?.addEventListener("close", this.handleDialogClose)

    if (this.keepTypedValue) {
      this.rememberTyped = () => { this.typed = this.element.value }
      this.restoreTyped = () => setTimeout(() => {
        if (!this.element.value && this.typed) this.element.value = this.typed
      }, 0)
      this.element.addEventListener("input", this.rememberTyped)
      this.element.addEventListener("blur", this.restoreTyped)
    }
  }

  disconnect() {
    this.dialog?.removeEventListener("close", this.handleDialogClose)
    if (this.rememberTyped) {
      this.element.removeEventListener("input", this.rememberTyped)
      this.element.removeEventListener("blur", this.restoreTyped)
    }

    if (this.picker) {
      this.picker.destroy();
      this.picker = null;
    }
  }

  parseTyped(text) {
    const value = text.trim()
    const german = value.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/)
    const iso = value.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/)
    const [ year, month, day ] = german ? [ german[3], german[2], german[1] ] : iso ? [ iso[1], iso[2], iso[3] ] : []
    if (!year) return undefined

    const date = new Date(Number(year), Number(month) - 1, Number(day))
    return date.getMonth() === Number(month) - 1 && date.getDate() === Number(day) ? date : undefined
  }

  closePicker() {
    this.picker?.close()
  }

  decorateCalendar(instance) {
    if (!instance?.calendarContainer) return

    instance.calendarContainer.classList.toggle("flatpickr-in-dialog", Boolean(this.dialog))
  }
}
